import { useState } from 'react';
import Image from 'next/image';
import { Stack, Typography } from '@mui/material';
import { authenticateStoreChannel } from '@/api/stores.api';
import type { LoginModalStatus } from '@/interfaces/component/modal.interface';
import type { ChannelId, CrawlingResultStatusFailType } from '@/interfaces/service/appstore/appstore.interface';
import { useFavoriteStore } from '@/stores/favoriteStore.store';
import { useToastStore } from '@/stores/toast.store';
import { getChannelLogoSrcById } from '@/utils/appstore.util';
import BaseModal from '@/components/modal/BaseModal';
import ErrorContents from '@/components/modal/channel/components/ErrorContents';
import LoginContents from '@/components/modal/channel/components/LoginContents';
import LoginInputs from '@/components/modal/channel/components/LoginInputs';

interface OwnershipVerificationModalProps {
  showModal: boolean;
  handleShowModal: () => void;
  storeName: string;
  storeId?: number;
  onLoginSuccess?: (teamAuthId: string) => void;
}

const OwnershipVerificationModal = ({ showModal, handleShowModal, storeName, storeId, onLoginSuccess }: OwnershipVerificationModalProps) => {
  const { setShowToast, setToastInfos } = useToastStore();
  const { appUserStoreInfo } = useFavoriteStore();

  const [loginData, setLoginData] = useState<{ id: string; password: string }>({
    id: '',
    password: ''
  });
  const [loginStatus, setLoginStatus] = useState<LoginModalStatus>('DEFAULT');
  const [contentsMode, setContentsMode] = useState<'LOGIN' | 'ERROR'>('LOGIN');
  const [errorStatus, setErrorStatus] = useState<CrawlingResultStatusFailType>();
  const channelId: ChannelId = 'NAVER_MAP';
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const validate = (id: string, password: string): boolean => {
    if (id.trim() === '') {
      setToastInfos({ text: '아이디를 입력해주세요.' });
      setShowToast(true);
      return false;
    }
    if (password.trim() === '') {
      setToastInfos({ text: '비밀번호를 입력해주세요.' });
      setShowToast(true);
      return false;
    }
    return true;
  };

  // 로그인 및 인증 처리
  const handleLogin = async (accountId: string, accountPassword: string) => {
    // props로 전달받은 storeId 또는 appUserStoreInfo에서 가져온 storeId 사용
    const currentStoreId = storeId || (appUserStoreInfo?.storeId ? Number(appUserStoreInfo.storeId) : null);

    if (!validate(accountId, accountPassword) || !currentStoreId) {
      setToastInfos({ text: '병원 정보가 없습니다.' });
      setShowToast(true);
      return;
    }

    try {
      setIsAuthenticating(true);
      setLoginStatus('LOGIN_PROGRESS');

      const authResult = await authenticateStoreChannel(currentStoreId, channelId, accountId, accountPassword);

      if (!authResult.isSuccess || !authResult.data) {
        return;
      }

      if (authResult.data.result === 'SUCCESS') {
        setLoginStatus('LOGIN_SUCCESS');

        // 성공 콜백 호출 - 부모 컴포넌트에서 팀 등록/조직 생성 모달 표시 처리
        if (onLoginSuccess && authResult.data.teamAuthId) {
          onLoginSuccess(authResult.data.teamAuthId);
        }

        // 모달 닫기
        handleShowModal();
      } else {
        setLoginStatus('LOGIN_FAIL');
        setToastInfos({
          text: authResult.data.status === 'ID_PASSWORD_ERROR' ? '아이디 또는 비밀번호가 올바르지 않습니다.' : `인증에 실패했습니다. (${authResult.data.status})`
        });
        setShowToast(true);
      }
    } catch (error) {
      setLoginStatus('LOGIN_FAIL');
      setToastInfos({ text: '로그인 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const onChangeIdInput = (e: InputEvent) => {
    const id = (e.target as HTMLInputElement)?.value ?? '';
    setLoginData({ ...loginData, id: id.toLocaleLowerCase() });
  };

  const onChangePwInput = (e: InputEvent) => {
    const password = (e.target as HTMLInputElement)?.value ?? '';
    setLoginData({ ...loginData, password });
  };

  const loginButton = {
    title: '계정 연동하기',
    style: { margin: '0 20px 20px 20px' },
    disabled: loginStatus === 'LOGIN_PROGRESS' || isAuthenticating,
    onClick: async () => {
      const { id, password } = loginData;
      await handleLogin(id, password);
    }
  };

  const modeData = {
    LOGIN: {
      component: (
        <>
          <LoginContents
            onChangeIdInput={onChangeIdInput}
            onChangePwInput={onChangePwInput}
            status={loginStatus}
            customPrimaryText="소유권의 인증을 위해 네이버 플레이스 ID와 비밀번호로 로그인을 진행해주세요."
            customSecondaryText="해당 정보는 암호화되어 저장되며, 사용 종료 시 자동 삭제됩니다."
          />
          <Stack direction="row" alignItems="top" sx={{ mt: 2 }}>
            <Image src={getChannelLogoSrcById(channelId)} width={20} height={20} alt="네이버" style={{ marginRight: '8px' }} />
            <Typography variant="subtitle2">소유권 확인을 위해 플레이스 로그인이 필요합니다.</Typography>
          </Stack>
        </>
      ),
      button: loginButton
    },
    ERROR: {
      component: errorStatus ? (
        <ErrorContents type={errorStatus} channelId={channelId}>
          <LoginInputs onChangeIdInput={onChangeIdInput} onChangePwInput={onChangePwInput} />
        </ErrorContents>
      ) : (
        <></>
      ),
      button: loginButton
    }
  };

  const handleCloseModal = () => {
    setLoginData({ id: '', password: '' });
    setLoginStatus('DEFAULT');
    setContentsMode('LOGIN');
    setErrorStatus(undefined);
    handleShowModal();
  };

  if (!appUserStoreInfo && !storeId) return null;

  return (
    <BaseModal
      showModal={showModal}
      handleShowModal={handleCloseModal}
      modalTitle="소유권 검증"
      buttons={{
        primary: modeData[contentsMode].button
      }}
      style={{ maxWidth: '446px', height: 'auto' }}
      isFullHeight
    >
      <Stack gap="24px" width="100%" paddingX="20px">
        {modeData[contentsMode].component}
      </Stack>
    </BaseModal>
  );
};

export default OwnershipVerificationModal;
